import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

const CUSTOM_SCHEME_PREFIXES = [
  'ch.pickapeak.ios://auth/callback',
  'ch.pickapeak.app://auth/callback',
] as const;

const UNIVERSAL_LINK_HOSTS = new Set([
  'pickapeak.ch',
  'www.pickapeak.ch',
  'staging.pickapeak.ch',
]);

const AUTH_CALLBACK_PATHS = new Set([
  '/auth/callback',
  '/auth/callback/ios',
  '/auth/callback/android',
]);

/** Handles OAuth + password-recovery deep links on native. */
@Injectable({ providedIn: 'root' })
export class NativeOAuthService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);

  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || !Capacitor.isNativePlatform()) {
      return;
    }

    void App.addListener('appUrlOpen', (event) => {
      void this.handleDeepLink(event.url);
    }).then((handle) => {
      this.destroyRef.onDestroy(() => {
        void handle.remove();
      });
    });

    void App.getLaunchUrl().then((result) => {
      if (result?.url) {
        void this.handleDeepLink(result.url);
      }
    });
  }

  static isAuthDeepLink(url: string): boolean {
    if (CUSTOM_SCHEME_PREFIXES.some((prefix) => url.startsWith(prefix))) {
      return true;
    }

    try {
      const parsed = new URL(url);
      if (!UNIVERSAL_LINK_HOSTS.has(parsed.hostname)) {
        return false;
      }
      const path = parsed.pathname.replace(/\/+$/, '') || '/';
      return AUTH_CALLBACK_PATHS.has(path);
    } catch {
      return false;
    }
  }

  private async handleDeepLink(url: string): Promise<void> {
    if (!NativeOAuthService.isAuthDeepLink(url)) {
      return;
    }

    try {
      await Browser.close();
    } catch {
      // Browser may already be dismissed.
    }

    const query = url.includes('?') ? url.slice(url.indexOf('?')) : '';
    const hash = !url.includes('?') && url.includes('#') ? url.slice(url.indexOf('#')) : '';
    await this.router.navigateByUrl(`/auth/callback${query}${hash}`, { replaceUrl: true });
  }
}
